import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicInlineLinkSx,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    let isActive = true;

    const validateResetToken = async () => {
      try {
        await api.get(`/auth/reset-password/${token}`);
        if (isActive) {
          setTokenValid(true);
        }
      } catch (requestError) {
        if (isActive) {
          setError('Invalid or expired reset link. Please request a new one.');
          setTokenValid(false);
        }
      } finally {
        if (isActive) {
          setValidating(false);
        }
      }
    };

    validateResetToken();

    return () => {
      isActive = false;
    };
  }, [token]);

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      navigate('/login');
    }, 2500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [navigate, success]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/auth/reset-password/${token}`, { password });
      setSuccess(true);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicPageFrame>
      <PublicCardHeader
        eyebrow="Reset password"
        title="Choose a new password"
        description="Set a new password for your account and continue where you left off."
      />

      {validating ? (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Validating your reset link...
          </Typography>
        </Stack>
      ) : !tokenValid ? (
        <Stack spacing={3}>
          <Alert severity="error">{error}</Alert>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button component={Link} to="/forgot-password" variant="contained" sx={publicPrimaryButtonSx}>
              Request new reset link
            </Button>
            <Button component={Link} to="/login" variant="outlined" sx={publicSecondaryButtonSx}>
              Back to login
            </Button>
          </Box>
        </Stack>
      ) : success ? (
        <Stack spacing={2.5} alignItems="flex-start">
          <Alert severity="success" icon={<CheckCircle2 size={18} />}>
            Password reset successful. Redirecting you to login...
          </Alert>
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily, lineHeight: 1.72 }}>
            Your account is ready. You can use your new password as soon as the redirect completes.
          </Typography>
          <Button component={Link} to="/login" endIcon={<ArrowRight size={16} />} sx={{ ...publicInlineLinkSx, px: 0 }}>
            Go to login now
          </Button>
        </Stack>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              required
              fullWidth
              name="password"
              label="New password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              helperText="Use at least 8 characters."
              sx={publicTextFieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              required
              fullWidth
              name="confirmPassword"
              label="Confirm new password"
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              sx={publicTextFieldSx}
            />
            <Button type="submit" fullWidth variant="contained" disabled={loading} sx={publicPrimaryButtonSx}>
              {loading ? 'Resetting password...' : 'Reset password'}
            </Button>
          </Stack>
        </Box>
      )}
    </PublicPageFrame>
  );
}

export default ResetPasswordPage;
